/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { Task } from 'ember-concurrency';
import Component from '@glimmer/component';
import ObjectModel from 'cio/models/object';
import { tracked } from '@glimmer/tracking';
import { queryEncode, safeQueryDecode } from 'cio/helpers/query';
import { action } from '@ember/object';
import { SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';
import { Filters, isEmptyFiltersQuery } from 'cio/utils/filters';
import { ConditionType } from 'cio/utils/condition-types';
import ObjectTypeModel from 'cio/models/object-type';
import { inject as service } from '@ember/service';
import AuthService from 'cio/services/auth';
import { FlyMouseEvent } from '@customerio/fly-components/types/events';
import rangeSelectionHelper from 'cio/utils/range-selection-helper';
import DS from 'ember-data';
import ObjectTypesService from 'cio/services/object-types';

interface Args {
  selectedObjects: ObjectModel[];
  setSelectedObjects: (c: ObjectModel[]) => unknown;
  loadObjectsTask: Task<ObjectModel[], [string?]>;
  totalObjectsRecs: number;
  objectsPerPage: number;
  objectType?: ObjectTypeModel;
  objectTypes: ObjectTypeModel[];
  isLoading: boolean;
  objectFilters: { filters: string; page: number; name: string };
  setObjectFilters: (f: { filters?: string; page?: number; name?: string }) => unknown;
}

export default class ObjectListComponent extends Component<Args> {
  @service auth!: AuthService;
  @service store!: DS.Store;
  @service objectTypes!: ObjectTypesService;

  @tracked allSelected = false;
  @tracked shiftSelectStart: number | null = null;
  @tracked shiftSelectEnd: number | null = null;
  @tracked selectedObjectTypeId = this.args.objectTypes.firstObject?.id;
  @tracked activeSearchBy = this.args.objectFilters.filters ? 'filter' : 'name';

  get objectTypeOptions() {
    return this.objectTypes.sortedObjectTypes.map((objectType) => ({
      label: objectType.name,
      value: objectType.id,
    }));
  }

  get objectType() {
    return this.args.objectType ?? this.store.peekRecord('object-type', this.selectedObjectTypeId ?? 1);
  }

  get objects() {
    return this.args.loadObjectsTask.lastSuccessful?.value ?? [];
  }

  get filterConditions() {
    return safeQueryDecode(this.args.objectFilters.filters ?? '');
  }

  get conditionListButtonLabel() {
    let name = this.objectType?.name.toLowerCase() ?? 'object';

    return {
      [ConditionType.ObjectAttribute]: `Filter by ${name} attribute`,
    };
  }

  get isAllSelected() {
    return (
      (this.args.totalObjectsRecs && this.args.totalObjectsRecs === this.args.selectedObjects.length) ||
      this.allSelected
    );
  }

  get allObjectsOnPageSelected() {
    if (this.args.selectedObjects == null || this.objects.length === 0) {
      return false;
    }

    if (this.isAllSelected && this.args.totalObjectsRecs > this.args.selectedObjects.length) {
      return false;
    }

    return this.args.selectedObjects.length === this.objects.length;
  }

  get allObjectsOnPageIndeterminate() {
    if (this.isAllSelected && this.args.totalObjectsRecs > this.args.selectedObjects.length) {
      return false;
    }

    return (this.args.selectedObjects?.length ?? 0) > 0 && !this.allObjectsOnPageSelected;
  }

  get hasFilters() {
    return this.args.objectFilters.filters.length > 0;
  }

  @action
  updateFilters(conditions: SerializedLegacyFilter[] | Filters) {
    let filterString = queryEncode(conditions);

    // Reset the query param to nothing
    if (isEmptyFiltersQuery(filterString)) {
      this.clearFilters();
    } else {
      this.args.setObjectFilters({ filters: filterString });
    }

    this.args.setObjectFilters({ page: 1 });
    this.refreshObjectsList();
  }

  @action
  clearFilters() {
    this.args.setObjectFilters({ filters: '' });
    this.clearSelection();
    this.refreshObjectsList();
  }

  @action
  clearSelection() {
    this.args.setSelectedObjects([]);
    this.allSelected = false;
  }

  @action
  selectAllObjectsOnPage(checked: boolean) {
    if (checked) {
      this.args.setSelectedObjects(this.objects.slice());
      this.allSelected = this.objects.length === this.args.totalObjectsRecs;
    } else {
      this.clearSelection();
    }
  }

  @action
  onPageChange(page: number) {
    this.args.setObjectFilters({ page });
    this.allSelected = false;
    this.args.setSelectedObjects([]);
    this.refreshObjectsList();
  }

  @action
  objectChecked(
    obj: ObjectModel,
    index: number,
    checked: boolean,
    metadata: { event: FlyMouseEvent<HTMLInputElement> },
  ) {
    let tempSelectedObjects = this.args.selectedObjects.slice();

    let { tempShiftSelectEnd, tempShiftSelectStart, allSelected } = rangeSelectionHelper<ObjectModel>({
      models: this.objects,
      singleModel: obj,
      selectedItems: tempSelectedObjects,
      index,
      checked,
      totalRecs: this.args.totalObjectsRecs,
      metadata,
      shiftSelectStart: this.shiftSelectStart,
      shiftSelectEnd: this.shiftSelectEnd,
    });

    this.shiftSelectStart = tempShiftSelectStart;
    this.shiftSelectEnd = tempShiftSelectEnd;
    this.allSelected = allSelected;
    this.args.setSelectedObjects(tempSelectedObjects);
  }

  @action
  onObjectTypeChange(objectTypeId: string) {
    this.selectedObjectTypeId = objectTypeId;
    this.clearSelection();
    this.refreshObjectsList(objectTypeId);
  }

  @action
  activateSearchBy(activeSearchBy: 'name' | 'filter') {
    if (this.activeSearchBy !== activeSearchBy) {
      let { filters, name } = this.args.objectFilters;
      this.activeSearchBy = activeSearchBy;
      this.args.setObjectFilters({ filters: '', page: 1, name: '' });
      if (filters !== '' || name !== '') {
        this.refreshObjectsList();
        this.clearSelection();
      }
    }
  }

  @action
  onSearch(name: string) {
    this.args.setObjectFilters({ name, page: 1 });
    this.clearSelection();
    this.refreshObjectsList();
  }

  private refreshObjectsList(objectTypeId?: string) {
    this.args.loadObjectsTask.perform(objectTypeId ?? this.objectType?.id);
  }
}
